import { useMotionValue, useSpring, useTransform, type MotionValue } from 'framer-motion'
import { sidepanelSideSign, type SidepanelSide } from '@shared/sidepanel/side'

/**
 * Shared geometry + slide animation for the edge peek trigger
 * (`peek-trigger.tsx` via `EdgePeekButton`).
 *
 * All values are expressed for a right-anchored panel; a left-anchored panel
 * mirrors by negating the x translate and swapping the `right` inset for `left`.
 */

const PEEK_AMOUNT = 40
const TRIGGER_WIDTH = 40 // size-10 = 2.5rem
const TRIGGER_LEFT_OFFSET = 16 // left-4 = 1rem

/** Edge inset (px) — `right` on a right-anchored panel, `left` on a left-anchored one. */
export const TRIGGER_EDGE_INSET = PEEK_AMOUNT - TRIGGER_LEFT_OFFSET - TRIGGER_WIDTH

/** Resting X (px) when fully hidden / fully slid in (right-anchored values). */
export const TRIGGER_HIDDEN_X = 60
export const TRIGGER_VISIBLE_X = -14

const TRIGGER_SPRING = { stiffness: 400, damping: 30, mass: 0.2 } as const

interface UseEdgeApproachXOptions {
  /** 0 → 1 proximity to the edge (from {@link useEdgeProximity}). */
  proximity: MotionValue<number>
  /** Which screen edge the trigger lives on (the sidepanel's anchored side). */
  side: SidepanelSide
  /** When true the trigger is pinned fully in regardless of proximity. */
  pinnedOpen?: boolean
}

/**
 * Maps edge proximity to the trigger's springy horizontal slide. `pinnedOpen`
 * forces it fully in regardless of proximity — used at low sensitivity (no
 * proximity zone) and for onboarding's force-peek fallback. Proximity is tracked
 * continuously, so `max(proximity, open)` only ever rises/falls smoothly.
 */
export function useEdgeApproachX({
  proximity,
  side,
  pinnedOpen = false,
}: UseEdgeApproachXOptions): MotionValue<number> {
  // Sync the pinned value during render (not in an effect) so it tracks
  // `pinnedOpen` without a frame of lag against the live proximity value.
  const open = useMotionValue(pinnedOpen ? 1 : 0)
  open.set(pinnedOpen ? 1 : 0)

  const sign = sidepanelSideSign(side)
  const target = useTransform(
    [proximity, open],
    ([p, o]: number[]) => sign * (TRIGGER_HIDDEN_X + Math.max(p, o) * (TRIGGER_VISIBLE_X - TRIGGER_HIDDEN_X)),
  )
  return useSpring(target, TRIGGER_SPRING)
}
